import { useMemo } from 'react';

import { ConfigService, Constants } from '@packages/core/config';

import { useAuthState } from '../auth';
import { getContainer } from '../container';
import { buildUserRoles, resolveBackend, resolveEnvironment, resolveHost } from './runtime.helpers';

/**
 * All runtime context fields. Static fields are available immediately; dynamic fields
 * (account data) are empty strings until authentication completes.
 */
export type RuntimeContext = {
    /** The backend environment the app is connected to ('development', 'stage', 'production'). */
    backend: string;

    /** The user's primary email address, or empty string before auth. */
    emailAddress: string;

    /** The resolved environment ('development', 'stage', 'production'). */
    environment: string;

    /** The deployment target ('local', 'preview', 'development', 'stage', 'production'). */
    host: string;

    /** Whether the user is authenticated. */
    isAuthenticated: boolean;

    /** The platform identifier ('web', 'mobile'). */
    platform: string;

    /** The user's account ID, or empty string before auth. */
    userId: string;

    /** The user's SOA username, or empty string before auth. */
    username: string;

    /** Comma-separated, deduped, sorted roleIds, or empty string before auth. */
    userRoles: string;
};

// Resolve static values once at module scope
const configService = getContainer().resolve(ConfigService);
const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
const environment = resolveEnvironment(configService.debugEnvName);
const host = resolveHost(environment, hostname);
const apiBaseUrl = configService.get(Constants.Env.ApiBaseUrl) ?? '';
const backend = resolveBackend(host, apiBaseUrl);

/**
 * Returns the full runtime context, combining static environment data with dynamic account data.
 * Static fields are available immediately; account fields populate after authentication.
 *
 * @returns the runtime context object
 */
export function useRuntimeContext(): RuntimeContext {
    const { account } = useAuthState();

    return useMemo(() => {
        const isAuthenticated = Boolean(account?.accountId);

        return {
            backend,
            emailAddress: account?.emailAddresses?.[0]?.emailAddress ?? '',
            environment,
            host,
            isAuthenticated,
            platform: 'web',
            userId: account?.accountId ?? '',
            username: account?.soaUsername ?? '',
            userRoles: isAuthenticated ? buildUserRoles(account?.baseRoles ?? [], account?.addOnRoles ?? []) : '',
        };
    }, [account?.accountId, account?.emailAddresses, account?.soaUsername, account?.baseRoles, account?.addOnRoles]);
}
